import { motion } from "framer-motion";
import { Gavel, Crown, LineChart, Scale, CloudLightning, Film, ArrowUpRight } from "lucide-react";
import SectionHeader from "./SectionHeader";

const EVENTS = [
  {
    id: "shark-tank",
    title: "Shark Tank",
    epithet: "The Council of Titans",
    description:
      "Have you got the next big idea for a business? Make a splash and present your idea to a group of top \"sharks.\" This premier competition challenges aspiring young entrepreneurs to pitch innovative, well-structured business ideas — demonstrating creativity, critical thinking, and commercial potential.",
    icon: Crown,
    meta: [
      { k: "Team", v: "2–5 participants" },
      { k: "Pitch", v: "5 min + Q&A" },
      { k: "Cost", v: "₹500 / team" },
      { k: "Submission", v: "Aug 4 · tradella.harvest@gmail.com" },
    ],
    judging: ["Innovation & Creativity", "Feasibility of Business Model", "Clarity & Persuasion", "Teamwork & Presentation"],
  },
  {
    id: "market-mayhem",
    title: "Market Mayhem",
    epithet: "The Marketplace of Olympus",
    description:
      "Step onto the trading floor where every decision counts. Navigate a volatile market driven by live company pitches, breaking news, and unpredictable shifts in stock value. Trust will be tested, instincts challenged, and the smartest traders will rise above the noise. Buy, sell, hold — and sometimes gamble — to build the highest-value portfolio by the final round.",
    icon: LineChart,
    meta: [
      { k: "Team", v: "2–4 participants" },
      { k: "Cost", v: "₹500 / team" },
    ],
    judging: ["Portfolio Performance", "Decision Justification", "Crisis Handling & Adaptability"],
  },
  {
    id: "ipl-auction",
    title: "IPL Auction",
    epithet: "The Assembly of Champions",
    description:
      "Step into the war room of the ultimate cricket franchise auction. Compete to build the perfect squad by bidding on a pool of players with a limited budget. Every decision counts — from strategic bidding to tactical squad composition. Bid smart. Play bold. Own the game.",
    icon: Gavel,
    meta: [
      { k: "Team", v: "2–5 participants (allotted in advance)" },
      { k: "Budget", v: "₹120 Cr" },
      { k: "Squad", v: "14 min · 17 max" },
      { k: "Cost", v: "₹500 / team" },
    ],
    judging: ["Team Composition & Balance", "Budget Management", "Strategic Bidding & Justification", "Presentation & Communication", "Adaptability"],
  },
  {
    id: "reel-chemy",
    title: "Reel-Chemy",
    epithet: "Lights. Camera. Convert.",
    description:
      "Step into the high-stakes world of marketing with Reel-Chemy — a high-energy, on-the-spot marketing challenge. Teams receive a surprise product and must craft a compelling, creative Instagram Reel–style ad to promote it within a limited timeframe.",
    icon: Film,
    meta: [
      { k: "Team", v: "2–4 participants" },
      { k: "Product", v: "Given on the spot" },
      { k: "Task", v: "30–60 sec reel" },
      { k: "Cost", v: "₹500 / team" },
    ],
    judging: ["Innovation & Creativity", "Marketing Appeal", "Product Understanding", "Execution"],
  },
  {
    id: "court-justice",
    title: "Consumer Court of Justice",
    epithet: "The Court of Athena",
    description:
      "Step into the legal arena in this immersive mock-court simulation featuring 4 unique cases. Participants act as Plaintiffs or Defendants in real-world business disputes, arguing their side using logic, evidence, and consumer rights. May the best lawyer win.",
    icon: Scale,
    meta: [
      { k: "Team", v: "2–5 participants" },
      { k: "Cases", v: "4 unique disputes" },
      { k: "Cost", v: "₹500 / team" },
    ],
    judging: ["Quality of Arguments", "Articulation", "Research", "Legal Expertise"],
  },
  {
    id: "crisis-cabinet",
    title: "Crisis Cabinet",
    epithet: "The Trial of the Gods",
    description:
      "A fast-paced crisis simulation centered on finance, regulation, and corporate strategy. Through directives, participants shape an unscripted real-world scenario, with every decision influencing the cabinet's course. Success depends on strategic thinking, negotiation, and adaptability. The agenda will be released ahead of the conference for preparation.",
    icon: CloudLightning,
    meta: [
      { k: "Team", v: "2–3 participants" },
      { k: "Cost", v: "₹500 / team" },
    ],
    judging: ["Decision Making", "Quality of Directives", "Financial Literacy", "Realism & Articulation"],
  },
];

function EventCard({ ev, idx }) {
  const Icon = ev.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: idx * 0.06 }}
      className="group relative p-6 sm:p-8 border border-[#B8873B]/20 hover:border-[#B8873B]/70 hover:-translate-y-1 transition-all duration-500 bg-[#0c0c0c] flex flex-col"
      data-testid={`event-card-${ev.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 border border-[#B8873B]/50 grid place-items-center group-hover:border-[#B8873B] transition-colors">
          <Icon size={22} className="text-[#B8873B]" strokeWidth={1.3} />
        </div>
        <ArrowUpRight className="text-[#B8873B]/60 group-hover:text-[#B8873B] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500" size={18} />
      </div>

      <h3 className="mt-6 font-display font-light text-marble text-2xl md:text-3xl leading-tight">{ev.title}</h3>
      <p className="mt-1 italic text-[#B8873B]/85 font-display text-base">— {ev.epithet}</p>
      <p className="mt-4 text-white/60 font-light leading-relaxed text-sm">{ev.description}</p>

      {/* Meta */}
      <div className="mt-5 space-y-1.5 border-t border-[#B8873B]/15 pt-4">
        {ev.meta.map((m) => (
          <div key={m.k} className="flex text-xs">
            <span className="w-24 shrink-0 text-[10px] tracking-[0.25em] uppercase text-[#B8873B]/70 pt-0.5">{m.k}</span>
            <span className="text-white/70">{m.v}</span>
          </div>
        ))}
      </div>

      {/* Judging */}
      <div className="mt-4 border-t border-[#B8873B]/15 pt-4">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8873B]/70 mb-2">Judging Criteria</div>
        <ul className="space-y-1">
          {ev.judging.map((j) => (
            <li key={j} className="text-xs text-white/60 flex gap-2">
              <span className="text-[#B8873B] mt-0.5">·</span>
              <span>{j}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}

export default function Events() {
  return (
    <section id="events" data-testid="events-section" className="relative py-20 sm:py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
        <SectionHeader
          eyebrow="Events"
          title={<><span className="text-marble">Six Arenas. </span><span className="italic text-gold-gradient">One Crown.</span></>}
          subtitle="Each contest is forged from a different domain of the modern market. Cost: ₹500 per team per event."
          testId="events-header"
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {EVENTS.map((ev, idx) => (
            <EventCard key={ev.id} ev={ev} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
